/* =====================================================================
   INNER PAGE TEXT — Platform, Company, Projects, Contact, Demo, Privacy, 404.
   English (en) and Korean (ko). Keep the same structure in both languages.
   [[words]] = highlight (rare). Let text wrap naturally: don't add "\n"
   to headings. A blank line ("\n\n") forces a deliberate break.
   `media` values are slot names from src/site/media.js.
   Status values: "current" (available now) | "planned" (in development).
   ===================================================================== */

export const pages = {
  en: {
    common: {
      home: "Home",
      breadcrumb: "Breadcrumb",
      onThisPage: "On this page",
      contactUs: "Contact KSS",
      orEmail: "or email",
      copy: "Copy",
      copied: "Copied",
      current: "Available now",
      demo: "Shown in the simulation",
      planned: "In development",
      filmClose: "Close",
      filmTitle: "KSS brand film",
    },

    /* ------------------------------ PLATFORM ------------------------------ */
    platform: {
      hero: {
        media: "platform.hero", crumb: "Platform", eyebrow: "KSS platform",
        title: "Hardware on the machine. Software for every role.",
        lead: "KS-101 hardware collects signals where the sewing happens. K SMART V2 software validates them and shows each role what it needs. Start with one module on one line and add the rest when it proves useful.",
        onpage: [["products", "KS-101 and K SMART V2"], ["modules", "Four modules"], ["demo", "Three points of view"], ["integrate", "Your existing systems"], ["deploy", "How we deploy"]],
      },
      products: {
        eyebrow: "Hardware + software",
        title: "Two parts, one record.",
        intro: "The hardware and the software are designed together, so every count on a screen can be traced back to a machine, an operator and a bundle.",
        items: [
          { name: "KS-101", kind: "Hardware at the machine", body: "Reads sewing machine signals such as stitches, thread trims and stops, and gives operators a terminal to log in, select size and confirm bundles.", points: ["Fitted to existing sewing machines", "Operator login and bundle confirmation", "Signals sent as they happen"] },
          { name: "K SMART V2", kind: "Software for the factory", body: "Checks each signal against the style's work standard, stores it with line, operation, order and time, and shows it in role-specific views.", points: ["Work-standard validation", "Operator, supervisor, manager and brand views", "Connections to MES and ERP"] },
        ],
        specs: "Technical specifications are available on request.",
        demoLink: "See the views in the demo",
      },
      modules: {
        title: "Four modules. Start with one.",
        intro: "Each module works on its own and becomes more useful as the next one is added. Three are available today; decision intelligence is in development.",
        exampleLabel: "Factory example",
        items: [
          { media: "mod.capture", num: "01", name: "Connected data capture", status: "current", headline: "Record production events where they happen.",
            body: "KS-101 signal units, operator terminals and system connections record each stitch, trim, stop and completed bundle.",
            features: ["Machine signal interfaces", "Operator input at the station", "Bundle completion logic", "Real-time event flow"],
            example: "An operator completes a bundle of 24 shirts. That event becomes a production record." },
          { media: "mod.data", num: "02", name: "Manufacturing data layer", status: "current", headline: "One consistent record for every operation.",
            body: "Each event is checked against the work standard and linked to the right line, order and time, so every view works from the same data.",
            features: ["Data validation", "Work standards", "Operation mapping", "ERP integration"],
            example: "The 24 shirts are matched to Line 3, order KSS-104 and the sleeve-attach operation." },
          { media: "mod.apps", num: "03", name: "Operational applications", status: "current", headline: "The right screen for every role.",
            body: "Operators, supervisors, managers and brands each get a clear view of output, quality and order progress, built on the same records.",
            features: ["Production analysis", "Quality management", "Line visibility", "Operational dashboards"],
            example: "The manager sees Line 3 move from 216 to 240 pieces; the brand sees the order reach 48%." },
          { media: "mod.intel", num: "04", name: "Decision intelligence", status: "planned", headline: "From what happened to what to do next.",
            body: "Validated history becomes the basis for line balancing, simulation, forecasts and factory-specific AI modules.",
            features: ["Line-balancing suggestions", "Simulation and digital twin", "Forecasts", "Custom AI modules"],
            example: "Suggest moving one operator to the bottleneck before the afternoon shift. Try a simulated version in the demo." },
        ],
      },
      demo: {
        eyebrow: "Role views",
        title: "One data foundation. Three points of view.",
        intro: "Switch roles to see how the same production records become a work screen, a control view and a delivery view. Sample data, not a live factory.",
        note: "SAMPLE DATA",
        open: "Open the interactive demo",
        views: [
          { media: "demo.operator", role: "Operator", tag: "Station terminal", text: "What the operator sees at the sewing machine.",
            stats: [["Target / hour", "42 pcs"], ["Completed today", "296 pcs"], ["Current bundle", "24 pcs"], ["Work standard", "4 steps"]] },
          { media: "demo.manager", role: "Factory manager", tag: "Line overview", text: "Line flow, stopped stations and waiting work across the floor.",
            stats: [["Active lines", "6 / 8"], ["Line 3 rate", "60 pcs/h"], ["Bottleneck", "Sleeve attach"], ["Output today", "8,620 pcs"]] },
          { media: "demo.brand", role: "Brand and buyer", tag: "Order progress", text: "Order progress and delivery outlook from real output.",
            stats: [["Order KSS-104", "48%"], ["Made so far", "240 / 500"], ["Delivery outlook", "On track"], ["Quality checks", "96% passed"]] },
        ],
      },
      integrate: {
        title: "Works with the systems you already run.",
        intro: "KSS sits between the floor and your business systems. Nothing has to be replaced on day one.",
        core: "validate, structure, share",
        inLabel: "Comes in",
        outLabel: "Goes out",
        inputs: [["⚙", "Sewing machines", "KS-101 signal units"], ["▣", "Operator terminals", "login, size, bundle"], ["≡", "Existing MES", "work orders, routings"], ["₩", "ERP and orders", "styles, quantities, dates"]],
        outputs: [["◔", "Operator view", "target against actual"], ["▤", "Line and factory view", "flow, stoppages, waiting work"], ["◎", "Brand order page", "progress and outlook"], ["⇩", "Reports and exports", "MES, ERP, BI"]],
      },
      deploy: {
        title: "Start with one line. Build confidence. Then expand.",
        intro: "Every deployment follows the same four steps, so you see real results on a single line before committing to more.",
        deliverable: "Example result",
        steps: [
          { title: "Discover", sub: "Understand the operation", body: "Walk the floor with your team. Map operations, machines, data sources and the decisions you want to improve, then agree on one line and a clear goal.", out: "A practical pilot plan", outBody: "Line 3, shirts: track completed bundles and find where delays start." },
          { title: "Connect", sub: "Set up a focused pilot", body: "Fit KS-101 hardware and terminals on the chosen line, load styles and work standards, and connect the systems you already use.", out: "A connected pilot", outBody: "Every station on Line 3 sends validated bundle records." },
          { title: "Validate", sub: "Check it in real production", body: "Compare records with actual output, test the workflow with supervisors and resolve gaps before agreeing on wider use.", out: "A reviewed pilot", outBody: "Counts match the floor, and the team knows what to change." },
          { title: "Expand", sub: "Roll out what works", body: "Add lines, sites and modules in stages, based on what the pilot proved.", out: "A staged rollout plan", outBody: "More lines next, then a second site." },
        ],
      },
      cta: { title: "Which module would help your line first?", body: "Tell us about one line and the decision you would most like to improve. We'll suggest where to start.", button: "Contact KSS" },
    },

    /* ------------------------------ COMPANY ------------------------------ */
    company: {
      hero: {
        media: "company.hero", crumb: "Company", eyebrow: "Company",
        title: "Connected factory data, [[less guesswork.]]",
        lead: "K SMART SYSTEMS is a manufacturing technology company connecting equipment, people and production information. We start with garment production and aim to serve more manufacturing industries.",
        onpage: [["purpose", "Our purpose"], ["principles", "How we work"], ["direction", "Our direction"], ["film", "Brand film"]],
      },
      purpose: {
        eyebrow: "Our purpose",
        title: "Factories already hold the answers.",
        lead: "They are spread across machines, paper and business systems. We bring them together in one place.",
        body: "Machine records, reports from workers and information in business systems rarely meet. KSS connects them so teams can understand production and decide on the same day, not the next week.",
        diagram: { inputs: ["Machine records", "Worker reports", "Business systems"], output: "Production overview", caption: "Three separate sources become one production overview." },
      },
      principles: {
        eyebrow: "How we work",
        title: "Understand the factory. Build the tools it needs.",
        intro: "Four habits behind every project we take on.",
        items: [
          ["Start with the factory", "Understand the people, equipment and daily production process on the floor, not in a meeting room."],
          ["Keep information clear", "Check and organise production information consistently, so it supports analysis today and AI later."],
          ["Connect existing systems", "Design tools that work with the machines and systems a factory already uses."],
          ["Grow step by step", "Review how the tools work on the floor and add features only when they are needed."],
        ],
      },
      direction: {
        title: "Start with garments. Reach more industries.",
        intro: "Garment production is one of the hardest places to measure. What works there can work in many other factories.",
        stops: [
          { tag: "Today", title: "Connect garment production", body: "Production records, work instructions, quality checks and system connections.", now: true },
          { tag: "Next", title: "Tools for each factory", body: "Analysis shaped by each factory's needs, including line balancing and simulation." },
          { tag: "Later", title: "More industries", body: "Bring what we learn on the sewing floor to other manufacturing fields." },
        ],
      },
      film: { media: "company.film", tag: "Brand film, 2 minutes", title: "From Signal to Intelligence", play: "Watch the film", note: "Contains illustrative scenes and sample screens." },
      cta: { title: "Build the next manufacturing interface with us.", body: "Whether you run a factory or build technology for one, we would like to hear from you.", button: "Contact KSS" },
    },

    /* ------------------------------ PROJECTS ------------------------------ */
    projects: {
      hero: {
        media: "projects.hero", crumb: "Projects", eyebrow: "Projects and pilot work",
        title: "From the sewing line to a shared picture of production.",
        lead: "How a pilot is scoped, the ways companies work with KSS, and the principles behind every project.",
        onpage: [["pilot", "Pilot 001"], ["areas", "Ways to work with us"], ["partners", "Partnership model"], ["principles", "Project principles"]],
      },
      pilot: {
        label: "Pilot 001, draft",
        title: "Every bundle has a next step.",
        facts: [
          ["The problem", "Production updates are recorded in separate places, so order progress is hard to follow."],
          ["The approach", "Connect completed bundles to a production line and a customer order."],
          ["Who uses it", "Operators record output. Managers review line and order progress."],
          ["Status", "Scope and records shown here are illustrative until project details are confirmed."],
        ],
        ticket: {
          top: "Try one production update", sample: "Sample data", record: "Production record", bundle: "Bundle", sub: "24 shirts, sewing complete",
          meta: "Line 3 · Order KSS-104 · Shirts", of: "of 500 pieces", completion: "Order completion",
          button: "Record a bundle (+24 pieces)", reset: "Reset",
          hint: "One entry updates the line and the order.", updated: "Line 3 and order KSS-104 updated together.",
          done: "Order complete. The brand sees 100%.", full: "Order complete. Reset to try again.",
        },
      },
      areas: {
        title: "Three ways to work with KSS.",
        intro: "From connecting one line to developing analysis modules together.",
        items: [
          { media: "area.production", title: "Connected production", status: "current", body: "Connect selected production flows and create real-time visibility from source data.", chips: ["Line-level data capture", "Production and quality views", "Connection to existing systems"] },
          { media: "area.data", title: "Data foundation", status: "current", body: "Define repeatable data models that connect operations, sites and management views.", chips: ["Operational data standards", "Structure across operations", "Scalable module design"] },
          { media: "area.intel", title: "Intelligence collaboration", status: "planned", body: "Develop analytics and AI modules with domain and technology partners.", chips: ["Use-case definition", "Model-ready data pipelines", "Factory-specific modules"] },
        ],
      },
      partners: {
        eyebrow: "Partnership model",
        title: "Different expertise. One connected system.",
        roles: [
          ["Manufacturers", "Define the production needs.", "Share the workflow, on-site constraints and priorities for the pilot."],
          ["Technology partners", "Contribute specialist expertise.", "Systems, analytics and integration where there is a clear use case and suitable data."],
          ["KSS", "Holds it together.", "Aligns responsibilities, defines the shared data structure and coordinates the connections."],
        ],
        diagram: { a: "MANUFACTURERS", b: "TECH PARTNERS", c1: "SHARED DATA", c2: "FOUNDATION", c: "Shared data foundation" },
      },
      principles: {
        title: "The principles behind every project.",
        intro: "Clear scope, agreed responsibilities and progress supported by evidence.",
        items: [
          ["Start with a clear problem", "Agree on the operational problem, project scope and success criteria first."],
          ["Respect information and ownership", "Agree on data access, intellectual property and commercial boundaries before work begins."],
          ["Validate before expanding", "Review the results of a focused pilot before committing to a wider rollout."],
          ["Make it repeatable", "Document the process and responsibilities so proven work can be applied elsewhere."],
        ],
      },
      cta: { title: "Bring operations, data and analysis together.", body: "Manufacturer or technology partner, tell us what you would like to build.", button: "Contact KSS" },
    },

    /* ------------------------------ CONTACT ------------------------------ */
    contact: {
      crumb: "Contact", eyebrow: "Contact",
      title: "Let's talk about your factory.",
      lead: "Tell us about your production process, existing systems or partnership idea. Choose a topic to start.",
      topicsLabel: "Topic",
      topics: [
        { icon: "◇", title: "Products and deployment", text: "The platform and how a pilot works." },
        { icon: "⇄", title: "Technology partnerships", text: "Project areas and the partnership model." },
        { icon: "◎", title: "Company inquiries", text: "About KSS, our purpose and direction." },
      ],
      emailLabel: "Email",
      replyLabel: "Reply time",
      reply: "We aim to reply within two business days.",
      form: {
        title: "Your message",
        required: "All fields are required.",
        name: "Name", namePh: "Your name", company: "Company", companyPh: "Company name",
        email: "Email", emailPh: "name@company.com", topic: "Topic", message: "Message",
        messagePh: "Your factory, systems or partnership idea",
        // button text depends on how the form is connected (REACT_APP_INQUIRY_MODE)
        submitMailto: "Open in your email app",
        submitApi: "Send message",
        sending: "Sending…",
        retry: "Try again",
        noteMailto: "This opens your email app with the message filled in. Nothing is sent until you press Send there, and nothing is stored on this website.",
        noteApi: "Your message is sent to the KSS team and stored so we can reply. See the privacy notice.",
        okMailto: "Your email app should now be open with the message ready. Press Send there to deliver it. If nothing opened, email us directly at the address on this page.",
        okApi: "Message sent. We'll reply to the address you gave.",
        errors: {
          name: "Enter your name.",
          company: "Enter your company name.",
          email: "Enter an email address like name@company.com.",
          message: "Enter a message.",
          summary: "Check the highlighted fields.",
          timeout: "The server took too long to respond. Your message was not sent. Try again, or email us directly.",
          network: "The message could not reach our server. Check your connection and try again, or email us directly.",
          rate: "Too many messages from this connection in the last hour. Email us directly instead.",
          invalid: "The server could not accept the message. Check the fields and try again.",
          server: "Our server had a problem and the message was not sent. Try again in a few minutes, or email us directly.",
        },
      },
      next: [
        ["We reply", "A short email to understand your line and goals."],
        ["A short call", "We walk through the platform and the demo together."],
        ["A floor visit", "We map one line and propose a pilot plan."],
      ],
      nextLabel: "What happens next",
    },

    /* ------------------------------ DEMO ------------------------------ */
    demo: {
      hero: {
        media: "demo.hero", crumb: "Demo", eyebrow: "Platform demo",
        title: "Try the platform on sample data.",
        lead: "Two demonstrations, both running on illustrative sample data and not connected to a factory: a simulated digital twin of a sewing line, and the screens each role uses.",
        onpage: [["twin", "Digital twin"], ["platform-demo", "Role views"]],
      },
    },

    /* ------------------------------ PRIVACY ------------------------------ */
    privacy: {
      crumb: "Privacy", eyebrow: "Privacy", title: "Privacy notice",
      intro: "This website does not use cookies, analytics or tracking scripts.",
      sections: [
        ["Contact form", "By default the contact form opens your own email app with your message filled in; nothing is sent to or stored by this website. If KSS switches the form to direct sending, your name, company, email address, topic and message are stored on KSS's server so we can reply, and are used only for that purpose."],
        ["Local preferences", "Your language choice (EN / KR) is saved in your browser so it is remembered on your next visit. It is never sent to a server."],
        ["Third-party services", "Web fonts are loaded from Google Fonts. Google may record the request according to its own privacy policy."],
        ["Contact", "For privacy questions, write to Contact@ksmartsystems.com."],
      ],
      updated: "Last updated: October 2026",
    },

    /* ------------------------------ 404 ------------------------------ */
    notFound: {
      title: "This page could not be found.",
      body: "The address may be mistyped, or the page has moved.", back: "Go to the home page",
    },
  },

  /* =============================== 한국어 =============================== */
  ko: {
    common: {
      home: "홈", breadcrumb: "현재 위치", onThisPage: "이 페이지에서", contactUs: "KSS에 문의하기", orEmail: "또는 이메일",
      copy: "복사", copied: "복사됨",
      current: "현재 제공", demo: "시뮬레이션에서 확인", planned: "개발 중", filmClose: "닫기", filmTitle: "KSS 브랜드 필름",
    },

    platform: {
      hero: {
        media: "platform.hero", crumb: "플랫폼", eyebrow: "KSS 플랫폼",
        title: "재봉기엔 하드웨어, 역할마다 소프트웨어.",
        lead: "KS-101 하드웨어가 재봉이 일어나는 곳에서 신호를 수집하고, K SMART V2 소프트웨어가 이를 검증해 각 역할에 필요한 화면으로 보여줍니다. 한 라인의 한 모듈로 시작해 효과가 확인되면 넓혀가세요.",
        onpage: [["products", "KS-101과 K SMART V2"], ["modules", "네 가지 모듈"], ["demo", "세 가지 관점"], ["integrate", "기존 시스템 연동"], ["deploy", "도입 방식"]],
      },
      products: {
        eyebrow: "하드웨어 + 소프트웨어",
        title: "두 부분, 하나의 기록.",
        intro: "하드웨어와 소프트웨어를 함께 설계했기 때문에 화면의 모든 수치를 재봉기, 작업자, 번들까지 추적할 수 있습니다.",
        items: [
          { name: "KS-101", kind: "재봉기의 하드웨어", body: "스티치, 실 끊김(트림), 정지 같은 재봉기 신호를 읽고, 작업자가 로그인하고 사이즈를 선택하며 번들을 확인하는 단말을 제공합니다.", points: ["기존 재봉기에 부착", "작업자 로그인과 번들 확인", "신호를 발생 즉시 전송"] },
          { name: "K SMART V2", kind: "공장을 위한 소프트웨어", body: "각 신호를 스타일의 작업 표준과 대조하고, 라인·공정·오더·시간과 함께 저장해 역할별 화면으로 보여줍니다.", points: ["작업 표준 기반 검증", "작업자·반장·관리자·브랜드 화면", "MES·ERP 연동"] },
        ],
        specs: "기술 사양은 요청 시 제공합니다.",
        demoLink: "데모에서 화면 보기",
      },
      modules: {
        title: "네 가지 모듈. 하나부터 시작하세요.",
        intro: "각 모듈은 단독으로 작동하며, 다음 모듈이 더해질수록 더 유용해집니다. 세 가지는 현재 제공 중이고, 의사결정 인텔리전스는 개발 중입니다.",
        exampleLabel: "현장 예시",
        items: [
          { media: "mod.capture", num: "01", name: "연결된 데이터 수집", status: "current", headline: "생산 이벤트를 발생 현장에서 기록합니다.",
            body: "KS-101 신호 장치, 작업자 단말, 시스템 연동으로 스티치, 트림, 정지, 완료 번들을 기록합니다.",
            features: ["재봉기 신호 인터페이스", "공정 단말의 작업자 입력", "번들 완료 로직", "실시간 이벤트 흐름"],
            example: "작업자가 셔츠 24장 번들을 완료하면, 그 이벤트가 생산 기록이 됩니다." },
          { media: "mod.data", num: "02", name: "제조 데이터 레이어", status: "current", headline: "모든 공정에 일관된 하나의 기록.",
            body: "각 이벤트를 작업 표준과 대조하고 올바른 라인, 오더, 시간과 연결해 모든 화면이 같은 데이터를 사용합니다.",
            features: ["데이터 검증", "작업 표준", "공정 매핑", "ERP 연동"],
            example: "셔츠 24장이 3라인, 오더 KSS-104, 소매 부착 공정에 연결됩니다." },
          { media: "mod.apps", num: "03", name: "운영 애플리케이션", status: "current", headline: "역할마다 알맞은 화면.",
            body: "작업자, 반장, 관리자, 브랜드가 같은 기록을 바탕으로 생산량, 품질, 오더 진행 상황을 명확하게 확인합니다.",
            features: ["생산 분석", "품질 관리", "라인 가시성", "운영 대시보드"],
            example: "관리자는 3라인이 216장에서 240장으로 늘어난 것을, 브랜드는 오더가 48%에 도달한 것을 확인합니다." },
          { media: "mod.intel", num: "04", name: "의사결정 인텔리전스", status: "planned", headline: "일어난 일에서 다음에 할 일로.",
            body: "검증된 이력이 라인 밸런싱, 시뮬레이션, 예측, 공장별 AI 모듈의 기반이 됩니다.",
            features: ["라인 밸런싱 제안", "시뮬레이션·디지털 트윈", "예측", "맞춤형 AI 모듈"],
            example: "오후 근무 전에 작업자 한 명을 병목 공정으로 옮기도록 제안합니다. 데모에서 시뮬레이션으로 체험해 보세요." },
        ],
      },
      demo: {
        eyebrow: "역할별 화면",
        title: "하나의 데이터 기반, 세 가지 관점.",
        intro: "역할을 바꿔 같은 생산 기록이 작업 화면, 관리 화면, 납기 화면으로 어떻게 바뀌는지 확인하세요. 실제 공장과 연결되지 않은 샘플 데이터입니다.",
        note: "샘플 데이터",
        open: "인터랙티브 데모 열기",
        views: [
          { media: "demo.operator", role: "작업자", tag: "공정 단말", text: "재봉기 앞에서 작업자가 보는 화면.",
            stats: [["시간당 목표", "42장"], ["오늘 완료", "296장"], ["현재 번들", "24장"], ["작업 표준", "4단계"]] },
          { media: "demo.manager", role: "공장 관리자", tag: "라인 현황", text: "현장 전체의 라인 흐름, 정지 공정, 대기 재공.",
            stats: [["가동 라인", "6 / 8"], ["3라인 속도", "시간당 60장"], ["병목", "소매 부착"], ["오늘 생산량", "8,620장"]] },
          { media: "demo.brand", role: "브랜드·바이어", tag: "오더 진행", text: "실제 생산량 기반의 오더 진행률과 납기 전망.",
            stats: [["오더 KSS-104", "48%"], ["현재 생산", "240 / 500"], ["납기 전망", "정상"], ["품질 검사", "96% 합격"]] },
        ],
      },
      integrate: {
        title: "이미 사용 중인 시스템과 함께 작동합니다.",
        intro: "KSS는 현장과 업무 시스템 사이에 위치합니다. 첫날부터 무언가를 교체할 필요가 없습니다.",
        core: "검증, 구조화, 공유",
        inLabel: "들어오는 정보",
        outLabel: "나가는 정보",
        inputs: [["⚙", "재봉기", "KS-101 신호 장치"], ["▣", "작업자 단말", "로그인, 사이즈, 번들"], ["≡", "기존 MES", "작업 지시, 공정 경로"], ["₩", "ERP·오더", "스타일, 수량, 일정"]],
        outputs: [["◔", "작업자 화면", "목표 대비 실적"], ["▤", "라인·공장 화면", "흐름, 정지, 대기 재공"], ["◎", "브랜드 오더 화면", "진행률과 전망"], ["⇩", "리포트·내보내기", "MES, ERP, BI"]],
      },
      deploy: {
        title: "한 라인에서 시작해, 확신을 쌓고, 확장합니다.",
        intro: "모든 도입은 같은 네 단계를 따릅니다. 더 큰 투자 전에 한 라인에서 실제 결과를 확인할 수 있습니다.",
        deliverable: "결과물 예시",
        steps: [
          { title: "진단", sub: "운영 이해", body: "고객 팀과 함께 현장을 보며 공정, 설비, 데이터 소스, 개선하고 싶은 의사결정을 정리하고 한 라인과 명확한 목표를 정합니다.", out: "실행 가능한 파일럿 계획", outBody: "3라인 셔츠 생산: 완료 번들을 추적하고 지연이 시작되는 곳을 찾습니다." },
          { title: "연결", sub: "집중 파일럿 구축", body: "선정한 라인에 KS-101 하드웨어와 단말을 설치하고, 스타일과 작업 표준을 등록한 뒤 기존 시스템과 연결합니다.", out: "연결된 파일럿", outBody: "3라인의 모든 공정이 검증된 번들 기록을 전송합니다." },
          { title: "검증", sub: "실제 생산에서 확인", body: "기록을 실제 생산량과 비교하고, 반장들과 업무 흐름을 시험하며, 확대 전에 차이를 해결합니다.", out: "검토를 마친 파일럿", outBody: "집계가 현장과 일치하고, 팀이 바꿀 점을 압니다." },
          { title: "확장", sub: "검증된 것을 확산", body: "파일럿에서 증명된 내용을 바탕으로 라인, 공장, 모듈을 단계적으로 추가합니다.", out: "단계별 확산 계획", outBody: "다음은 추가 라인, 이후 두 번째 공장." },
        ],
      },
      cta: { title: "어떤 모듈이 라인에 가장 먼저 도움이 될까요?", body: "한 라인과 가장 개선하고 싶은 의사결정을 알려주세요. 어디서 시작하면 좋을지 제안해 드리겠습니다.", button: "KSS에 문의하기" },
    },

    company: {
      hero: {
        media: "company.hero", crumb: "회사 소개", eyebrow: "회사 소개",
        title: "공장 데이터를 연결해 [[짐작을 줄입니다.]]",
        lead: "K SMART SYSTEMS는 설비, 사람, 생산 정보를 연결하는 제조 기술 기업입니다. 봉제 생산에서 시작해 더 많은 제조 산업을 지원하는 것을 목표로 합니다.",
        onpage: [["purpose", "우리의 목적"], ["principles", "일하는 방식"], ["direction", "방향"], ["film", "브랜드 필름"]],
      },
      purpose: {
        eyebrow: "우리의 목적",
        title: "공장은 이미 답을 가지고 있습니다.",
        lead: "다만 설비, 종이, 업무 시스템에 흩어져 있을 뿐입니다. 우리는 그것을 한곳에 모읍니다.",
        body: "설비 기록, 작업자 보고, 업무 시스템의 정보는 좀처럼 만나지 않습니다. KSS는 이를 연결해 팀이 생산을 이해하고 다음 주가 아닌 그날 판단할 수 있게 합니다.",
        diagram: { inputs: ["설비 기록", "작업자 보고", "업무 시스템"], output: "생산 현황", caption: "흩어진 세 가지 정보가 하나의 생산 현황이 됩니다." },
      },
      principles: {
        eyebrow: "일하는 방식",
        title: "공장을 이해하고, 필요한 도구를 만듭니다.",
        intro: "모든 프로젝트에 적용하는 네 가지 원칙.",
        items: [
          ["현장에서 시작합니다", "회의실이 아닌 현장에서 사람, 설비, 일상의 생산 과정을 먼저 이해합니다."],
          ["정보를 명확하게 유지합니다", "생산 정보를 일관되게 검증하고 정리해 오늘의 분석과 앞으로의 AI를 지원합니다."],
          ["기존 시스템과 연결합니다", "공장이 이미 사용하는 설비와 시스템과 함께 작동하는 도구를 설계합니다."],
          ["단계적으로 성장합니다", "현장에서 도구가 어떻게 쓰이는지 검토하고, 필요할 때만 기능을 더합니다."],
        ],
      },
      direction: {
        title: "봉제에서 시작해, 더 많은 산업으로.",
        intro: "봉제 생산은 가장 측정하기 어려운 현장 중 하나입니다. 이곳에서 통하는 방식은 다른 많은 공장에서도 통할 수 있습니다.",
        stops: [
          { tag: "현재", title: "봉제 생산 연결", body: "생산 기록, 작업 지시, 품질 검사, 시스템 연동.", now: true },
          { tag: "다음", title: "공장별 맞춤 도구", body: "라인 밸런싱과 시뮬레이션을 포함해 공장별 필요에 맞춘 분석." },
          { tag: "이후", title: "더 많은 제조 산업", body: "봉제 현장에서 배운 것을 다른 제조 분야로 확장." },
        ],
      },
      film: { media: "company.film", tag: "브랜드 필름, 2분", title: "신호에서 인텔리전스로", play: "영상 보기", note: "예시 장면과 샘플 화면이 포함되어 있습니다." },
      cta: { title: "다음 제조 인터페이스를 함께 만듭니다.", body: "공장을 운영하시든, 공장을 위한 기술을 만드시든 이야기를 듣고 싶습니다.", button: "KSS에 문의하기" },
    },

    projects: {
      hero: {
        media: "projects.hero", crumb: "프로젝트", eyebrow: "프로젝트 · 파일럿",
        title: "봉제 라인에서 모두가 함께 보는 생산 현황으로.",
        lead: "파일럿의 범위를 정하는 방법, KSS와 함께 일하는 방식, 그리고 모든 프로젝트의 원칙을 소개합니다.",
        onpage: [["pilot", "파일럿 001"], ["areas", "협력 방식"], ["partners", "파트너십 모델"], ["principles", "프로젝트 원칙"]],
      },
      pilot: {
        label: "파일럿 001, 초안",
        title: "모든 번들에는 다음 단계가 있습니다.",
        facts: [
          ["문제", "생산 업데이트가 여러 곳에 따로 기록되어 오더 진행 상황을 파악하기 어렵습니다."],
          ["접근", "완료된 번들을 생산 라인과 고객 오더에 연결합니다."],
          ["사용자", "작업자는 실적을 기록하고, 관리자는 라인과 오더 진행을 확인합니다."],
          ["상태", "프로젝트 세부 사항이 확정되기 전까지 여기 보이는 범위와 기록은 예시입니다."],
        ],
        ticket: {
          top: "생산 업데이트 체험", sample: "샘플 데이터", record: "생산 기록", bundle: "번들", sub: "셔츠 24장, 봉제 완료",
          meta: "3라인 · 오더 KSS-104 · 셔츠", of: "/ 500장", completion: "오더 진행률",
          button: "완료 번들 기록 (+24장)", reset: "초기화",
          hint: "한 번의 입력으로 라인과 오더가 함께 업데이트됩니다.", updated: "3라인과 오더 KSS-104가 함께 업데이트되었습니다.",
          done: "오더 완료. 브랜드 화면에 100%로 표시됩니다.", full: "오더 완료. 초기화 후 다시 시도하세요.",
        },
      },
      areas: {
        title: "KSS와 함께하는 세 가지 방식.",
        intro: "한 라인 연결부터 분석 모듈 공동 개발까지.",
        items: [
          { media: "area.production", title: "연결된 생산", status: "current", body: "선택한 생산 흐름을 연결해 원천 데이터 기반의 실시간 가시성을 만듭니다.", chips: ["라인 단위 데이터 수집", "생산·품질 화면", "기존 시스템 연동"] },
          { media: "area.data", title: "데이터 기반", status: "current", body: "공정, 공장, 관리 화면을 연결하는 반복 가능한 데이터 모델을 정의합니다.", chips: ["운영 데이터 표준", "공정 간 구조", "확장 가능한 모듈 설계"] },
          { media: "area.intel", title: "인텔리전스 협력", status: "planned", body: "도메인·기술 파트너와 함께 분석 및 AI 모듈을 개발합니다.", chips: ["활용 사례 정의", "모델용 데이터 파이프라인", "공장별 모듈"] },
        ],
      },
      partners: {
        eyebrow: "파트너십 모델",
        title: "서로 다른 전문성, 하나로 연결된 시스템.",
        roles: [
          ["제조사", "생산 요구 사항을 정의합니다.", "파일럿을 위한 업무 흐름, 현장 제약, 우선순위를 공유합니다."],
          ["기술 파트너", "전문 역량을 더합니다.", "명확한 활용 사례와 적합한 데이터가 있는 곳에 시스템, 분석, 연동을 제공합니다."],
          ["KSS", "전체를 하나로 묶습니다.", "역할을 조율하고, 공유 데이터 구조를 정의하며, 연결을 관리합니다."],
        ],
        diagram: { a: "제조사", b: "기술 파트너", c1: "공유 데이터", c2: "기반", c: "공유 데이터 기반" },
      },
      principles: {
        title: "모든 프로젝트의 원칙.",
        intro: "명확한 범위, 합의된 역할, 근거에 기반한 진행.",
        items: [
          ["명확한 문제에서 시작", "운영상의 문제, 프로젝트 범위, 성공 기준을 먼저 합의합니다."],
          ["정보와 소유권 존중", "작업 시작 전에 데이터 접근, 지식재산, 상업적 경계를 합의합니다."],
          ["확장 전 검증", "더 넓은 도입 전에 집중 파일럿의 결과를 검토합니다."],
          ["반복 가능하게", "검증된 작업을 다른 곳에도 적용할 수 있도록 과정과 역할을 문서화합니다."],
        ],
      },
      cta: { title: "운영, 데이터, 분석을 하나로.", body: "제조사든 기술 파트너든, 만들고 싶은 것을 알려주세요.", button: "KSS에 문의하기" },
    },

    contact: {
      crumb: "문의", eyebrow: "문의",
      title: "귀사의 공장에 대해 이야기해 주세요.",
      lead: "생산 공정, 기존 시스템, 파트너십 아이디어를 알려주세요. 먼저 주제를 선택해 주세요.",
      topicsLabel: "주제",
      topics: [
        { icon: "◇", title: "제품 및 도입", text: "플랫폼과 파일럿 진행 방식." },
        { icon: "⇄", title: "기술 파트너십", text: "프로젝트 분야와 파트너십 모델." },
        { icon: "◎", title: "회사 문의", text: "KSS의 목적과 방향." },
      ],
      emailLabel: "이메일",
      replyLabel: "회신",
      reply: "영업일 기준 2일 이내 회신을 목표로 합니다.",
      form: {
        title: "문의 내용",
        required: "모든 항목을 입력해 주세요.",
        name: "이름", namePh: "성함", company: "회사", companyPh: "회사명",
        email: "이메일", emailPh: "name@company.com", topic: "주제", message: "문의 내용",
        messagePh: "공장, 시스템, 파트너십 아이디어",
        submitMailto: "이메일 앱에서 열기",
        submitApi: "메시지 보내기",
        sending: "보내는 중…",
        retry: "다시 시도",
        noteMailto: "이메일 앱이 메시지가 입력된 상태로 열립니다. 그 앱에서 보내기를 눌러야 전송되며, 이 웹사이트에는 아무것도 저장되지 않습니다.",
        noteApi: "메시지는 회신을 위해 KSS 팀에 전송되고 저장됩니다. 개인정보 처리방침을 확인하세요.",
        okMailto: "이메일 앱이 메시지가 입력된 상태로 열렸습니다. 그 앱에서 보내기를 눌러 주세요. 앱이 열리지 않았다면 이 페이지의 주소로 직접 이메일을 보내 주세요.",
        okApi: "메시지를 보냈습니다. 입력하신 주소로 회신드리겠습니다.",
        errors: {
          name: "이름을 입력해 주세요.",
          company: "회사명을 입력해 주세요.",
          email: "name@company.com 형식의 이메일 주소를 입력해 주세요.",
          message: "문의 내용을 입력해 주세요.",
          summary: "표시된 항목을 확인해 주세요.",
          timeout: "서버 응답이 너무 늦어 메시지를 보내지 못했습니다. 다시 시도하거나 이메일로 직접 연락해 주세요.",
          network: "메시지가 서버에 도달하지 못했습니다. 인터넷 연결을 확인하고 다시 시도하거나 이메일로 직접 연락해 주세요.",
          rate: "최근 한 시간 동안 이 연결에서 보낸 메시지가 너무 많습니다. 이메일로 직접 연락해 주세요.",
          invalid: "서버가 메시지를 받을 수 없습니다. 입력 내용을 확인하고 다시 시도해 주세요.",
          server: "서버 문제로 메시지를 보내지 못했습니다. 잠시 후 다시 시도하거나 이메일로 직접 연락해 주세요.",
        },
      },
      next: [
        ["회신", "라인과 목표를 이해하기 위한 짧은 이메일."],
        ["짧은 통화", "플랫폼과 데모를 함께 살펴봅니다."],
        ["현장 방문", "한 라인을 함께 보고 파일럿 계획을 제안합니다."],
      ],
      nextLabel: "문의 후 진행 순서",
    },

    demo: {
      hero: {
        media: "demo.hero", crumb: "데모", eyebrow: "플랫폼 데모",
        title: "샘플 데이터로 플랫폼을 체험하세요.",
        lead: "두 가지 데모 모두 예시용 샘플 데이터로 작동하며 실제 공장과 연결되어 있지 않습니다. 봉제 라인의 시뮬레이션 디지털 트윈과 역할별 화면을 확인할 수 있습니다.",
        onpage: [["twin", "디지털 트윈"], ["platform-demo", "역할별 화면"]],
      },
    },

    privacy: {
      crumb: "개인정보", eyebrow: "개인정보", title: "개인정보 처리방침",
      intro: "이 웹사이트는 쿠키, 분석 도구, 추적 스크립트를 사용하지 않습니다.",
      sections: [
        ["문의 양식", "기본 설정에서 문의 양식은 메시지가 입력된 방문자의 이메일 앱을 열 뿐이며, 이 웹사이트로 전송되거나 저장되지 않습니다. KSS가 양식을 직접 전송 방식으로 바꾸면 회신을 위해 이름, 회사, 이메일 주소, 주제, 문의 내용이 KSS 서버에 저장되며 그 목적으로만 사용됩니다."],
        ["로컬 설정", "언어 선택(EN / KR)은 다음 방문 시 기억되도록 브라우저에 저장되며 서버로 전송되지 않습니다."],
        ["외부 서비스", "웹 폰트는 Google Fonts에서 불러옵니다. Google은 자체 개인정보 처리방침에 따라 요청을 기록할 수 있습니다."],
        ["문의", "개인정보 관련 문의는 Contact@ksmartsystems.com으로 보내주세요."],
      ],
      updated: "최종 업데이트: 2026년 10월",
    },

    notFound: {
      title: "페이지를 찾을 수 없습니다.",
      body: "주소가 잘못 입력되었거나 페이지가 이동되었습니다.", back: "홈으로 가기",
    },
  },
};
